import { ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { Match } from '../../models/match';
import { CommonModule } from '@angular/common';
import { MatchStatus } from '../../enums/matchStatus.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import _ from 'lodash';
import { TooltipModule } from 'primeng/tooltip';
import { MatchService } from '../../services/match.service';
import { LogService } from '../../services/log.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Tournament } from '../../models/tournament';
import { TournamentService } from '../../services/tournament.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-matches',
  imports: [
    CommonModule,
    TooltipModule,
    TranslateModule,
  ],
  templateUrl: './matches.component.html',
  styleUrl: './matches.component.scss',
})
export class MatchesComponent implements OnInit {
  @Input() consolation: boolean = false;
  @Input() isFinal: boolean = false;
  @Input() matches: Match[] = [];
  @Input() stage: string = 'ROUNDS';

  @Output() changePiecesEvent = new EventEmitter<Match>();
  @Output() updateEvent = new EventEmitter<Match>();

  private readonly destroy$ = inject(DestroyRef);
  public tournament: Tournament | undefined;

  constructor(
    public authService: AuthService,
    private cdr: ChangeDetectorRef,
    private logService: LogService,
    private matchService: MatchService,
    private tournamentService: TournamentService,
    private translate: TranslateService,
  ){}

  ngOnInit(): void {
    // get tournament
    this.tournament = this.tournamentService.tournament();
    // format matches
    this.matches = _.orderBy(_.map(this.matches, (match: Match) => {
      return {
        ...match, 
        editable: match.status !== MatchStatus.FINISHED,
        loserTeamId: match.status !== MatchStatus.FINISHED && 
          _.isNull(match.loserTeamId) ? undefined : match.loserTeamId,
        winnerTeamId: match.status !== MatchStatus.FINISHED && 
          _.isNull(match.winnerTeamId) ? undefined : match.winnerTeamId,
      };
    }), 'tableNumber');
  }

  public isWinner(teamId: string, match: Match): boolean {
    return match.winnerTeamId === teamId;
  }

  public isDraw(match: Match): boolean {
    return _.isNull(match.winnerTeamId) && _.isNull(match.loserTeamId);
  }

  public setMatchWinner(match: Match, winnerId: string | null, loserId: string | null): void {
    match.winnerTeamId = match.winnerTeamId === winnerId ? undefined : winnerId;
    match.loserTeamId = match.winnerTeamId !== undefined ? loserId : undefined;
  }

  public isDisabled(match: Match): boolean {
    // TODO: remove consolation condition when app ready for finishing tournament
    return !match.whiteTeamId || !match.blackTeamId || match.winnerTeamId === undefined || match.loserTeamId === undefined ||
      this.tournament?.stage !== this.stage || (this.tournament.status === 'FINISHED' && !this.consolation) ||
      !this.authService.hasRole(['admin', 'staff', 'referee']);
  }

  public changePieces(match: Match): void {
    if(!match) return;
    this.matchService.changePieces(match).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
      next: (res) => {
        this.logService.debug('[Matches] - changePieces', res);
        this.changePiecesEvent.emit(match);
      },
      error: (err) => {
        this.logService.error('[Matches] - changePieces', err, this.translate.instant('error.match-change-pieces'));
      }
    });
  }

  public edit(match: Match): void {
    match.editable = true;
  }

  public save(match: Match): void {
    const matchData = _.cloneDeep(match);
    delete matchData.editable;
    this.matchService.updateMatch(match).pipe(takeUntilDestroyed(this.destroy$)).subscribe({
      next: (res) => {
        this.logService.debug('[Matches] - updateMatch', res);
        match.editable = false;
        match.status = MatchStatus.FINISHED;
        this.cdr.markForCheck();
        this.updateEvent.emit(match);
      },
      error: (err) => {
        this.logService.error('[Matches] - updateMatch', err, this.translate.instant('error.match-update'));
      }
    });
  }
}
